import { useState } from "react";
import { ChevronDown, Loader2, Printer, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLabelPrinter } from "@/labels";
import { useZebraPrintEnabled } from "@/labels/print/useLabelPrintingSettings";
import { cn } from "@/lib/utils";

export type SparePartLabelPrintNamespace = "harvest" | "inventory" | "passport";

export interface SparePartLabelPrintMenuProps {
  namespace: SparePartLabelPrintNamespace;
  /** Button label when printing all labels; defaults to single-label copy when omitted. */
  mode?: "all" | "single";
  onPrintBrowser: () => void | Promise<void>;
  onPrintZebra?: () => void | Promise<void>;
  /** Override the derived i18n trigger label (e.g. admin storage "Print"). */
  label?: string;
  isZebraPrinting?: boolean;
  /** When omitted, availability is taken from `useLabelPrinter`. */
  isZebraAvailable?: boolean;
  disabled?: boolean;
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  /** Compact printer icon trigger (card headers). */
  iconOnly?: boolean;
  ariaLabel?: string;
}

function keyPrefixFor(namespace: SparePartLabelPrintNamespace): string {
  if (namespace === "harvest") return "process";
  if (namespace === "passport") return "print";
  return "labels";
}

/**
 * Layered print menu (browser + optional Zebra):
 *  - While company settings load → disabled placeholder (no flash)
 *  - When Zebra disabled → single browser button
 *  - When Zebra enabled → popover with Browser + Zebra rows
 */
export function SparePartLabelPrintMenu({
  namespace,
  mode = "single",
  onPrintBrowser,
  onPrintZebra,
  label,
  isZebraPrinting = false,
  isZebraAvailable: isZebraAvailableProp,
  disabled = false,
  variant = "outline",
  size = "default",
  className,
  iconOnly = false,
  ariaLabel,
}: SparePartLabelPrintMenuProps) {
  const { t } = useTranslation(namespace);
  const { enabled: zebraEnabled, loading: settingsLoading } = useZebraPrintEnabled();
  const { isZebraAvailable: probedAvailable, qzAvailable, probeQz } = useLabelPrinter();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const keyPrefix = keyPrefixFor(namespace);
  const triggerLabel =
    label ??
    (namespace === "passport"
      ? t("print.print")
      : mode === "all"
        ? t(`${keyPrefix}.${namespace === "harvest" ? "printAllLabels" : "printLabels"}`)
        : t(`${keyPrefix}.printSingleLabel`, {
            defaultValue: t("process.printSingleLabel", { ns: "harvest" }),
          }));

  const browserLabel = t(`${keyPrefix}.printBrowser`);
  const zebraLabel = t(`${keyPrefix}.printZebra`);
  const successMessage = t(`${keyPrefix}.zebraPrintSuccess`);
  const failureMessage = t(`${keyPrefix}.zebraPrintFailed`);
  const qzUnavailableHint = t(`${keyPrefix}.qzUnavailable`);

  const resolvedAvailable =
    isZebraAvailableProp ?? (qzAvailable === null ? probedAvailable : qzAvailable);
  const showZebraOption = !settingsLoading && zebraEnabled && !!onPrintZebra;
  const zebraReady = showZebraOption && resolvedAvailable;
  const printing = busy || isZebraPrinting;

  const handleBrowser = async () => {
    if (disabled || printing) return;
    setOpen(false);
    setBusy(true);
    try {
      await onPrintBrowser();
    } finally {
      setBusy(false);
    }
  };

  const handleZebra = async () => {
    if (disabled || printing || !onPrintZebra) return;
    if (!resolvedAvailable) {
      const ok = await probeQz();
      if (!ok) {
        toast.error(qzUnavailableHint);
        return;
      }
    }
    setOpen(false);
    setBusy(true);
    try {
      await onPrintZebra();
      toast.success(successMessage);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "PrintCancelledError") return;
      const message = err instanceof Error ? err.message : undefined;
      toast.error(message ? `${failureMessage}: ${message}` : failureMessage);
    } finally {
      setBusy(false);
    }
  };

  const triggerIcon = printing ? (
    <Loader2 className={cn(iconOnly ? "w-3.5 h-3.5" : "w-4 h-4", "animate-spin")} />
  ) : (
    <Printer className={iconOnly ? "w-3.5 h-3.5" : "w-4 h-4"} />
  );

  // Loading — disabled control matching final shape (avoid flash).
  if (settingsLoading) {
    if (iconOnly) {
      return (
        <Button
          type="button"
          variant={variant}
          size={size === "default" ? "icon" : size}
          disabled
          className={cn("h-8 w-8", className)}
          aria-label={ariaLabel ?? triggerLabel}
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        </Button>
      );
    }
    return (
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled
        className={cn("gap-2", className)}
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        {triggerLabel}
      </Button>
    );
  }

  // Zebra off → single browser button.
  if (!showZebraOption) {
    if (iconOnly) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant={variant}
              size={size === "default" ? "icon" : size}
              onClick={() => void handleBrowser()}
              disabled={disabled || printing}
              className={cn("h-8 w-8", className)}
              aria-label={ariaLabel ?? triggerLabel}
            >
              {triggerIcon}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{triggerLabel}</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    return (
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled || printing}
        className={cn("gap-2", className)}
        onClick={() => void handleBrowser()}
      >
        {triggerIcon}
        {triggerLabel}
      </Button>
    );
  }

  const triggerButton = iconOnly ? (
    <Button
      type="button"
      variant={variant}
      size={size === "default" ? "icon" : size}
      disabled={disabled || printing}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={ariaLabel ?? triggerLabel}
      className={cn("h-8 w-8", className)}
    >
      {triggerIcon}
    </Button>
  ) : (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={disabled || printing}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn("gap-2", className)}
    >
      {triggerIcon}
      {triggerLabel}
      <ChevronDown className="w-4 h-4 opacity-60" />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1" onOpenAutoFocus={(e) => e.preventDefault()}>
        <button
          type="button"
          disabled={disabled || printing}
          className={cn(
            "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
            "hover:bg-accent hover:text-accent-foreground",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
          onClick={() => void handleBrowser()}
        >
          <Printer className="w-4 h-4" />
          {browserLabel}
        </button>
        <button
          type="button"
          disabled={disabled || printing || !zebraReady}
          className={cn(
            "flex w-full flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-sm outline-none",
            "hover:bg-accent hover:text-accent-foreground",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
          onClick={() => void handleZebra()}
        >
          <span className="flex w-full items-center gap-2">
            <Tag className="w-4 h-4" />
            {zebraLabel}
          </span>
          {!zebraReady && (
            <span className="pl-6 text-xs text-muted-foreground">{qzUnavailableHint}</span>
          )}
        </button>
      </PopoverContent>
    </Popover>
  );
}
